import React, { useCallback, useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonText,
  IonSpinner,
  IonButton,
  IonBadge,
} from "@ionic/react";
import moment from "moment";
import Header from "src/components/Header";
import OperariosRemplazos from "src/services/operarioRemplazo";

const formatDate = (value?: string) =>
  value ? moment(value).format("DD/MM/YYYY") : "-";

// Etiqueta y color por estado de confirmacion (alineado con la app del operario).
const ESTADOS: Record<string, { label: string; color: string }> = {
  CONFIRMADO: { label: "Confirmado", color: "success" },
  RECHAZADO: { label: "Rechazado", color: "danger" },
  PENDIENTE: { label: "Pendiente", color: "warning" },
  VENCIDO: { label: "Vencido", color: "medium" },
  SIN_NOTIFICAR: { label: "Sin notificar", color: "light" },
};

const estadoInfo = (estado?: string) =>
  ESTADOS[String(estado || "").toUpperCase()] || { label: estado || "-", color: "light" };

const NotificacionReemplazos: React.FC = () => {
  const [fechaInicio, setFechaInicio] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [fechaFin, setFechaFin] = useState(
    moment().endOf("month").format("YYYY-MM-DD")
  );
  const [reemplazos, setReemplazos] = useState<any[]>([]);
  const [totales, setTotales] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp: any = await OperariosRemplazos.listarConfirmaciones({
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      });
      const data = resp?.data || {};
      setReemplazos(data.reemplazos || []);
      setTotales(data.totales || {});
    } catch (err: any) {
      console.error("Error al cargar notificacion de reemplazos:", err);
      setError(
        err?.response?.data?.error || "No se pudieron cargar los reemplazos."
      );
      setReemplazos([]);
      setTotales({});
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <IonPage>
      <Header titulo="Notificacion de Reemplazos" />
      <IonContent>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Filtros de fecha */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
              padding: 16,
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "flex-end",
            }}
          >
            <div style={{ flex: 1, minWidth: 130 }}>
              <div style={{ fontSize: 13, color: "#777", marginBottom: 4 }}>Desde</div>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                style={{ width: "100%", padding: "10px 8px", borderRadius: 8, border: "1px solid #ddd" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 130 }}>
              <div style={{ fontSize: 13, color: "#777", marginBottom: 4 }}>Hasta</div>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                style={{ width: "100%", padding: "10px 8px", borderRadius: 8, border: "1px solid #ddd" }}
              />
            </div>
            <IonButton
              onClick={cargar}
              disabled={loading}
              style={{ minWidth: 110, height: 44, fontWeight: 600 }}
            >
              Filtrar
            </IonButton>
          </div>

          {/* Chips de totales por estado */}
          {!loading && !error && reemplazos.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {Object.keys(totales).map((est) => {
                const info = estadoInfo(est);
                return (
                  <IonBadge key={`tot-${est}`} color={info.color} style={{ fontSize: 13, padding: "6px 10px" }}>
                    {info.label}: {totales[est]}
                  </IonBadge>
                );
              })}
            </div>
          ) : null}

          {/* Lista / estados */}
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 30 }}>
              <IonSpinner />
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <IonText color="danger">{error}</IonText>
            </div>
          ) : reemplazos.length === 0 ? (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <IonText color="medium">Sin reemplazos en el rango seleccionado.</IonText>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {reemplazos.map((r: any, idx: number) => {
                const info = estadoInfo(r.estado);
                return (
                  <div
                    key={`nr-${idx}`}
                    style={{
                      background: "#ffffff",
                      borderRadius: 12,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                      padding: 16,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 10,
                        marginBottom: 8,
                      }}
                    >
                      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#910434" }}>
                        {r.punto_servicio || "-"}
                      </h2>
                      <IonBadge color={info.color} style={{ fontSize: 13, padding: "6px 10px" }}>
                        {info.label}
                      </IonBadge>
                    </div>
                    <div style={{ fontSize: 15, color: "#222", lineHeight: 1.5 }}>
                      <div>
                        <span style={{ color: "#777" }}>Reemplazante: </span>
                        {r.nombre_reemplazante || r.legajo_reemplazante || "-"}
                      </div>
                      <div>
                        <span style={{ color: "#777" }}>Ausente: </span>
                        {r.nombre_reemplazado || r.legajo_reemplazado || "-"}
                      </div>
                      <div>
                        <span style={{ color: "#777" }}>Fecha: </span>
                        {formatDate(r.fecha_inicio)}
                        {r.fecha_fin && r.fecha_fin !== r.fecha_inicio
                          ? ` - ${formatDate(r.fecha_fin)}`
                          : ""}
                      </div>
                      {String(r.estado).toUpperCase() === "RECHAZADO" && r.motivo_rechazo ? (
                        <div style={{ marginTop: 6, color: "#910434" }}>
                          <span style={{ color: "#777" }}>Motivo del rechazo: </span>
                          {r.motivo_rechazo}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default NotificacionReemplazos;
